// Bilingual blog content. Each post is rendered at /blog/:slug (Turkish) and
// /en/blog/:slug (English). Keep slugs stable - they are part of the URL and
// are listed in package.json "reactSnap.include" and public/sitemap.xml.

export type BlogLang = "tr" | "en";

// A run of paragraph text. When `to` is set the segment renders as an internal
// react-router link (used for contextual in-body links to the pillar guides and
// landing pages); otherwise it is plain text. Keeps body copy declarative while
// still funnelling readers into the topic cluster.
export interface BlogTextSegment {
  text: string;
  to?: string;
}

export interface BlogBlock {
  type: "heading" | "paragraph" | "list";
  text?: string;
  // Rich paragraph alternative to `text`: an ordered list of plain-text and
  // internal-link segments. Only read for `type: "paragraph"`.
  segments?: BlogTextSegment[];
  items?: string[];
}

export interface BlogLocale {
  title: string;
  // Used as the meta description and the card excerpt on the blog index.
  description: string;
  body: BlogBlock[];
}

export interface BlogPost {
  slug: string; // Turkish slug, used at /blog/<slug>/
  enSlug: string; // English slug, used at /en/blog/<enSlug>/
  // ISO date (YYYY-MM-DD).
  date: string;
  tr: BlogLocale;
  en: BlogLocale;
}

export const blogPosts: BlogPost[] = [
  {
    slug: "akaryakit-istasyonu-nakit-akis-yonetimi",
    enSlug: "fuel-station-cash-flow-management",
    date: "2026-05-20",
    tr: {
      title: "Akaryakıt İstasyonu Nakit Akış Yönetimi: Kapsamlı Rehber",
      description:
        "Akaryakıt istasyonlarında nakit akışını kontrol altında tutmanın yolları: vardiya mutabakatı, banka eşleştirmesi ve denetlenebilir kayıtlar.",
      body: [
        {
          type: "paragraph",
          text: "Akaryakıt ve benzin istasyonları, gün içinde çok sayıda nakit ve kredi kartı işlemini aynı anda yönetir. Bu yoğunlukta nakit akışını kontrol altında tutmak, hem kârlılığı korumak hem de personel açıklarını erken fark etmek için kritiktir. Bu rehberde, istasyon nakit akışı yönetiminin temel adımlarını ele alıyoruz.",
        },
        {
          type: "heading",
          text: "Nakit akışı neden vardiya bazında izlenmeli?",
        },
        {
          type: "paragraph",
          text: "Bir istasyonda gün, genellikle birden fazla vardiyaya bölünür. Nakit akışını gün sonunda toplu olarak değil, her vardiya kapanışında izlemek, farkların hangi vardiyada ve hangi personelde oluştuğunu netleştirir. Vardiya bazlı izleme, sorunların kaynağını saatler sonra değil, anında ortaya çıkarır.",
        },
        {
          type: "heading",
          text: "Otomasyon satışlarını banka kayıtlarıyla eşleştirin",
        },
        {
          type: "paragraph",
          text: "Kredi kartı satışlarının gerçekten bankaya geçip geçmediğini doğrulamanın tek güvenilir yolu, ERP/otomasyon satışlarını banka kayıtlarıyla karşılaştırmaktır. Banka onaylı tutarlar, açıklayıcı nakit tutarlardan ayrı tutulduğunda her kuruşun izi sürülebilir hale gelir.",
        },
        {
          type: "list",
          items: [
            "Banka tarafından onaylanan kart tutarlarını ayrı izleyin.",
            "Nakit olarak girilmiş ancak kartla ödenmiş satışları düzeltin.",
            "Çözülmemiş farkları öne çıkarın ve sorumlu vardiyaya bağlayın.",
          ],
        },
        {
          type: "heading",
          text: "Personel açıklarını otomatik tespit edin",
        },
        {
          type: "paragraph",
          text: "Her vardiya banka onaylı ödemeler ve ERP toplamlarıyla mutabık kılındığında, oluşan farklar otomatik olarak ilgili vardiyaya ve personele atanabilir. Gerekçe, inceleyen ve zaman damgası kayıt altına alındığında açık yönetimi denetlenebilir bir sürece dönüşür.",
        },
        {
          type: "heading",
          text: "Manuel işten otomasyona geçiş",
        },
        {
          type: "paragraph",
          text: "Kredi kartı satışları banka onaylı kayıtlardan otomatik eşleştirilip onaylandığında, geriye yalnızca bankaya yatan nakdi doğrulamak kalır. Bu yaklaşım, daha önce 1,5-2 saat süren vardiya incelemesini saniyelere indirir ve manuel iş yükünü belirgin biçimde azaltır.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker, akaryakıt ve benzin istasyonları için bu akışı uçtan uca otomatikleştirir. Demo talep etmek için bizimle iletişime geçebilirsiniz.",
        },
      ],
    },
    en: {
      title: "Fuel Station Cash Flow Management: A Complete Guide",
      description:
        "How to keep fuel station cash flow under control: shift reconciliation, bank matching, and audit-ready records.",
      body: [
        {
          type: "paragraph",
          text: "Fuel and gas stations handle a high volume of cash and credit card transactions at the same time. Keeping cash flow under control at this pace is critical both to protect profitability and to catch employee deficits early. This guide walks through the core steps of station cash flow management.",
        },
        { type: "heading", text: "Why track cash flow shift by shift?" },
        {
          type: "paragraph",
          text: "A station's day is usually split into several shifts. Tracking cash flow at every shift close, rather than once at the end of the day, clarifies which shift and which employee a discrepancy came from. Shift-level tracking surfaces problems immediately instead of hours later.",
        },
        { type: "heading", text: "Match ERP sales with bank records" },
        {
          type: "paragraph",
          text: "The only reliable way to confirm that card sales actually reached the bank is to compare ERP/automation sales against bank records. When bank-confirmed amounts are kept separate from descriptive cash amounts, every amount becomes traceable.",
        },
        {
          type: "list",
          items: [
            "Track bank-confirmed card amounts separately.",
            "Correct sales entered as cash that were actually paid by card.",
            "Surface unresolved differences and tie them to the responsible shift.",
          ],
        },
        { type: "heading", text: "Detect employee deficits automatically" },
        {
          type: "paragraph",
          text: "When each shift is reconciled against bank-confirmed payments and ERP totals, the resulting differences can be attributed automatically to the relevant shift and employee. Recording the reason, reviewer, and timestamp turns deficit handling into an auditable process.",
        },
        { type: "heading", text: "From manual work to automation" },
        {
          type: "paragraph",
          text: "When card sales are matched and approved automatically from bank-confirmed records, all that remains is verifying the cash deposited to the bank. This approach cuts a shift review that used to take 1.5 to 2 hours down to seconds and noticeably lowers manual workload.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker automates this flow end to end for fuel and gas stations. Contact us to request a demo.",
        },
      ],
    },
  },
  {
    slug: "benzin-istasyonu-yazilimi-secimi",
    enSlug: "how-to-choose-gas-station-software",
    date: "2026-05-24",
    tr: {
      title: "Benzin İstasyonu Yazılımı Seçerken Nelere Dikkat Etmeli?",
      description:
        "Benzin istasyonu yazılımı seçerken değerlendirmeniz gereken kriterler: otomasyon entegrasyonu, mutabakat, raporlama ve denetlenebilirlik.",
      body: [
        {
          type: "paragraph",
          text: "Doğru benzin istasyonu yazılımı, günlük operasyonu kolaylaştırmanın ötesinde nakit akışınızı koruyan bir kontrol katmanıdır. Yanlış seçim ise manuel iş yükünü artırır ve farkların gözden kaçmasına yol açar. İşte değerlendirmeniz gereken temel kriterler.",
        },
        { type: "heading", text: "1. Otomasyon ve ERP entegrasyonu" },
        {
          type: "paragraph",
          text: "Yazılımın TURPAK gibi pompa otomasyon sistemleriyle ve kullandığınız ERP ile sorunsuz entegre olması gerekir. Satış verisi otomatik aktığında, manuel veri girişi kaynaklı hatalar ortadan kalkar.",
        },
        { type: "heading", text: "2. Banka mutabakatı yeteneği" },
        {
          type: "paragraph",
          text: "İyi bir yazılım, kredi kartı satışlarını banka kayıtlarıyla eşleştirip onaylayabilmelidir. Banka onaylı tutarların açıklayıcı nakit tutarlardan ayrılması, mutabakatı güvenilir kılar.",
        },
        { type: "heading", text: "3. Denetlenebilir kayıtlar" },
        {
          type: "paragraph",
          text: "Yapılan her düzeltmenin gerekçesi, inceleyeni ve zaman damgası saklanmalıdır. Denetlenebilirlik, hem iç kontrol hem de olası anlaşmazlıklarda kanıt sağlar.",
        },
        { type: "heading", text: "4. Aradığınız kriterleri özetleyen kontrol listesi" },
        {
          type: "list",
          items: [
            "Pompa otomasyonu ve ERP ile gerçek zamanlı entegrasyon",
            "Vardiya bazlı mutabakat ve personel açığı takibi",
            "Banka onaylı kart eşleştirmesi ve ödeme tipi düzeltmesi",
            "Çok istasyonlu yapı ve rol bazlı erişim",
            "Tarayıcıdan erişilebilen, kurulum gerektirmeyen web yazılımı",
          ],
        },
        {
          type: "paragraph",
          text: "Shift Tracker, bu kriterlerin tamamını tek bir akışta sunar. İhtiyaçlarınızı konuşmak için demo talep edebilirsiniz.",
        },
      ],
    },
    en: {
      title: "How to Choose Gas Station Software",
      description:
        "Key criteria for choosing gas station software: automation integration, reconciliation, reporting, and auditability.",
      body: [
        {
          type: "paragraph",
          text: "The right gas station software is more than a way to simplify daily operations; it is a control layer that protects your cash flow. The wrong choice increases manual workload and lets discrepancies slip through. Here are the core criteria to evaluate.",
        },
        { type: "heading", text: "1. Automation and ERP integration" },
        {
          type: "paragraph",
          text: "The software should integrate cleanly with pump automation systems such as TURPAK and with the ERP you use. When sales data flows in automatically, errors from manual data entry disappear.",
        },
        { type: "heading", text: "2. Bank reconciliation capability" },
        {
          type: "paragraph",
          text: "Good software should match and approve credit card sales against bank records. Separating bank-confirmed amounts from descriptive cash amounts makes reconciliation trustworthy.",
        },
        { type: "heading", text: "3. Audit-ready records" },
        {
          type: "paragraph",
          text: "Every correction should store its reason, reviewer, and timestamp. Auditability provides evidence for both internal control and any future disputes.",
        },
        { type: "heading", text: "4. A checklist of what to look for" },
        {
          type: "list",
          items: [
            "Real-time integration with pump automation and ERP",
            "Shift-level reconciliation and employee deficit tracking",
            "Bank-confirmed card matching and payment type correction",
            "Multi-station support with role-based access",
            "Browser-based software that needs no installation",
          ],
        },
        {
          type: "paragraph",
          text: "Shift Tracker delivers all of these in a single flow. Request a demo to discuss your needs.",
        },
      ],
    },
  },
  {
    slug: "vardiya-mutabakati-ve-personel-acigi",
    enSlug: "shift-reconciliation-employee-deficits",
    date: "2026-05-28",
    tr: {
      title: "Vardiya Mutabakatı ve Personel Açığı Yönetimi",
      description:
        "Akaryakıt istasyonlarında vardiya mutabakatının nasıl yapıldığı ve personel açıklarının denetlenebilir şekilde nasıl yönetileceği.",
      body: [
        {
          type: "paragraph",
          text: "Vardiya mutabakatı, bir vardiyada gerçekleşen satışların, tahsil edilen nakit ve kartların ve bankaya geçen tutarların birbiriyle tutarlı olduğunu doğrulama sürecidir. Doğru yapıldığında, personel açıkları tahmine değil kanıta dayanır.",
        },
        { type: "heading", text: "Mutabakat adımları" },
        {
          type: "list",
          items: [
            "Vardiyayı açın ve ilgili ERP ile banka verisini yükleyin.",
            "Yanlış girilmiş ödeme tiplerini düzeltin.",
            "Otomasyon satışlarını ve banka ödemelerini yan yana karşılaştırın.",
            "Tahsisleri oluşturun ve kalan tutarı çözüme bağlayın.",
          ],
        },
        { type: "heading", text: "Personel açığı nasıl ele alınır?" },
        {
          type: "paragraph",
          text: "Mutabakat sonunda ortaya çıkan fark, ilgili vardiyaya ve personele atanır. Bu farkın bir veri giriş hatası mı, bir ödeme tipi yanlışlığı mı yoksa gerçek bir açık mı olduğu, denetlenebilir kayıtlar sayesinde netleşir.",
        },
        { type: "heading", text: "Şeffaflık güveni artırır" },
        {
          type: "paragraph",
          text: "Her düzeltmenin gerekçesi ve sorumlusu kayıt altında olduğunda, personel açığı tartışmaları objektif bir zemine oturur. Bu şeffaflık, hem yönetim hem de saha ekibi için güven sağlar.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker, vardiya mutabakatını ve personel açığı takibini tek akışta birleştirir. Detaylar için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Shift Reconciliation and Employee Deficit Management",
      description:
        "How shift reconciliation works at fuel stations and how to manage employee deficits in an auditable way.",
      body: [
        {
          type: "paragraph",
          text: "Shift reconciliation is the process of confirming that the sales made during a shift, the cash and cards collected, and the amounts that reached the bank are all consistent with each other. Done correctly, employee deficits rest on evidence rather than guesswork.",
        },
        { type: "heading", text: "The reconciliation steps" },
        {
          type: "list",
          items: [
            "Open the shift and load the relevant ERP and bank data.",
            "Correct any payment types that were entered incorrectly.",
            "Compare ERP sales and bank payments side by side.",
            "Create allocations and resolve the remaining amount.",
          ],
        },
        { type: "heading", text: "How to handle an employee deficit" },
        {
          type: "paragraph",
          text: "The difference that appears at the end of reconciliation is attributed to the relevant shift and employee. Audit-ready records clarify whether that difference is a data entry error, a payment type mistake, or a genuine deficit.",
        },
        { type: "heading", text: "Transparency builds trust" },
        {
          type: "paragraph",
          text: "When the reason and owner of every correction are on record, deficit discussions rest on an objective basis. This transparency builds trust for both management and the field team.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker combines shift reconciliation and employee deficit tracking in a single flow. Contact us for details.",
        },
      ],
    },
  },
  {
    slug: "turpak-otomasyonu-banka-mutabakati",
    enSlug: "turpak-automation-vs-bank-reconciliation",
    date: "2026-06-02",
    tr: {
      title: "TURPAK Otomasyonunuz Var, Peki Banka Mutabakatı?",
      description:
        "Pompa otomasyonu satışı kaydeder; ancak paranın bankaya geçtiğini kanıtlamaz. TURPAK verisini banka onaylı kayıtlarla eşleştirerek mutabakat açığını kapatın.",
      body: [
        {
          type: "paragraph",
          text: "TURPAK, Asis ve benzeri pompa otomasyon sistemleri akaryakıt istasyonlarında satışın pompada doğru ölçülmesini ve kaydedilmesini sağlar. Ancak bir satışın otomasyonda görünmesi, o paranın gerçekten bankaya geçtiği anlamına gelmez. Mutabakat açığı tam burada başlar.",
        },
        { type: "heading", text: "Otomasyonun yaptığı ve yapmadığı" },
        {
          type: "paragraph",
          text: "Pompa ve tank otomasyonu; litre, stok, vardiya ve pompa-tank verisini üretmekte güçlüdür. Buna karşılık kredi kartı satışlarının bankaya yansıyıp yansımadığını, nakit girişlerinin doğruluğunu ve ödeme tiplerinin gerçeğe uygunluğunu banka kayıtlarıyla karşılaştırmaz.",
        },
        { type: "heading", text: "Mutabakat açığı nerede oluşur?" },
        {
          type: "list",
          items: [
            "Otomasyonda görünen bir kart satışı bankaya hiç geçmemiş olabilir.",
            "Nakit olarak girilen bir satış aslında kartla ödenmiş olabilir.",
            "Vardiya kapanışı pompa toplamına dayanır; banka onayına değil.",
          ],
        },
        { type: "heading", text: "Banka onaylı mutabakat ne ekler?" },
        {
          type: "paragraph",
          text: "Shift Tracker, otomasyondan gelen satış verisini banka onaylı ödemelerle yan yana eşleştirir. Yanlış girilen ödeme tiplerini düzeltir, banka onaylı tutarları açıklayıcı nakit tutarlardan ayırır ve her düzeltmeyi gerekçe, inceleyen ve zaman damgasıyla kaydeder.",
        },
        { type: "heading", text: "Otomasyon ve mutabakat birlikte" },
        {
          type: "paragraph",
          text: "Pompa otomasyonu satışı ölçer; Shift Tracker o satışın bankaya geçtiğini kanıtlar. İkisi birlikte, vardiya açıklarını ve suistimalleri görünür kılan eksiksiz bir kontrol oluşturur. Demo için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "You Have TURPAK Automation, But What About Bank Reconciliation?",
      description:
        "Pump automation records the sale; it doesn't prove the money reached the bank. Close the reconciliation gap by matching TURPAK data with bank-confirmed records.",
      body: [
        {
          type: "paragraph",
          text: "Pump automation systems like TURPAK and Asis make sure a sale is measured and recorded correctly at the pump. But a sale appearing in automation does not mean the money actually reached the bank. That is exactly where the reconciliation gap begins.",
        },
        { type: "heading", text: "What automation does, and doesn't do" },
        {
          type: "paragraph",
          text: "Pump and tank automation is strong at producing litre, inventory, shift and pump-tank data. What it does not do is compare your card sales against the bank to confirm they settled, check that cash figures are accurate, or verify that the reported payment types are truthful. Those checks need bank records.",
        },
        { type: "heading", text: "Where the reconciliation gap appears" },
        {
          type: "list",
          items: [
            "A card sale shown in automation may never have reached the bank.",
            "A sale entered as cash may actually have been paid by card.",
            "Shift close relies on pump totals, not on bank confirmation.",
          ],
        },
        { type: "heading", text: "What bank-confirmed reconciliation adds" },
        {
          type: "paragraph",
          text: "Shift Tracker matches the sales data coming from automation against bank-confirmed payments, side by side. It corrects misclassified payment types, keeps bank-confirmed amounts separate from descriptive cash amounts, and records every correction with its reason, reviewer and timestamp.",
        },
        { type: "heading", text: "Automation and reconciliation together" },
        {
          type: "paragraph",
          text: "Pump automation measures the sale; Shift Tracker proves that sale reached the bank. Together they form a complete control that makes shift deficits and fraud visible. Contact us for a demo.",
        },
      ],
    },
  },
  {
    slug: "turpak-satis-banka-eslestirme",
    enSlug: "matching-turpak-sales-with-bank-payments",
    date: "2026-06-01",
    tr: {
      title: "TURPAK Satışını Banka Ödemeleriyle Eşleştirme Rehberi",
      description:
        "Adım adım: TURPAK/otomasyon kart satışlarını banka onaylı ödemelerle nasıl eşleştirir, farkları nasıl çözer ve denetlenebilir kayıt oluşturursunuz.",
      body: [
        {
          type: "paragraph",
          text: "Otomasyon kart satışlarını kaydeder, ancak bu satışların bankaya geçtiğini doğrulamak ayrı bir adımdır. Bu rehber, TURPAK gibi sistemlerden gelen satış verisini banka onaylı ödemelerle eşleştirmenin pratik yolunu adım adım anlatır.",
        },
        { type: "heading", text: "Eşleştirme neden gerekir?" },
        {
          type: "paragraph",
          text: "Banka onayıyla karşılaştırılmayan bir satış toplamı, otomasyon içinde tutarlı görünse bile gerçeği yansıtmayabilir. Eşleştirme, her kart satışının banka tarafından onaylanmış bir kayda dayandığını güvence altına alır.",
        },
        { type: "heading", text: "Adım adım eşleştirme" },
        {
          type: "list",
          items: [
            "Vardiyayı açın ve ilgili otomasyon (TURPAK/Asis) satış verisini yükleyin.",
            "Kart ve nakit satışları ERP/otomasyon tarafında listeleyin.",
            "Banka onaylı ödemeleri yan yana karşılaştırın.",
            "Yanlış girilmiş ödeme tiplerini düzeltin (nakit ↔ kart).",
            "Tahsisleri oluşturun ve kalan tutarı çözüme bağlayın.",
            "Her adımı gerekçe, inceleyen ve zaman damgasıyla kaydedin.",
          ],
        },
        { type: "heading", text: "Sık karşılaşılan durumlar" },
        {
          type: "list",
          items: [
            "Nakit girilmiş ama kartla ödenmiş satış.",
            "Banka onaylı kısmi kart tutarı ve kalan nakit.",
            "Komşu vardiyaya ait olup yanlış vardiyada görünen ödeme.",
          ],
        },
        { type: "heading", text: "Sonuç" },
        {
          type: "paragraph",
          text: "Eşleştirme tamamlandığında her tutar ya banka onaylı bir kayda ya da açıklamalı bir nakit girişine bağlanır. Shift Tracker bu akışı otomatikleştirir; demo için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Matching TURPAK Sales with Bank Payments: A Step-by-Step Guide",
      description:
        "Step by step: how to match TURPAK/automation card sales with bank-confirmed payments, resolve differences, and produce an auditable record.",
      body: [
        {
          type: "paragraph",
          text: "Automation records card sales, but confirming those sales reached the bank is a separate step. This guide walks through the practical way to match sales data from systems like TURPAK against bank-confirmed payments.",
        },
        { type: "heading", text: "Why matching is needed" },
        {
          type: "paragraph",
          text: "A sales total that is never compared against bank confirmation may look consistent inside automation yet still not reflect reality. Matching guarantees that every card sale rests on a bank-confirmed record.",
        },
        { type: "heading", text: "The step-by-step match" },
        {
          type: "list",
          items: [
            "Open the shift and load the relevant automation (TURPAK/Asis) sales data.",
            "List the card and cash sales on the ERP/automation side.",
            "Compare bank-confirmed payments side by side.",
            "Correct any misclassified payment types (cash ↔ card).",
            "Create allocations and resolve the remaining amount.",
            "Record each step with its reason, reviewer and timestamp.",
          ],
        },
        { type: "heading", text: "Common cases" },
        {
          type: "list",
          items: [
            "A sale entered as cash that was actually paid by card.",
            "A bank-confirmed partial card amount with the remainder in cash.",
            "A payment that belongs to a neighboring shift but shows in the wrong one.",
          ],
        },
        { type: "heading", text: "The outcome" },
        {
          type: "paragraph",
          text: "Once matching is done, every amount traces to a bank-confirmed record or an explained cash entry. Shift Tracker automates this flow. Contact us for a demo.",
        },
      ],
    },
  },
  {
    slug: "kart-satisini-nakit-gosterme-suistimali",
    enSlug: "recording-card-sale-as-cash-fraud",
    date: "2026-05-31",
    tr: {
      title: "Kart Satışını Nakit Gösterme: Yaygın Bir İstasyon Suistimali",
      description:
        "Kart satışının nakit (veya nakdin kart) gösterilmesi nasıl bir suistimaldir, neden fark edilmez ve banka onaylı mutabakatla nasıl engellenir?",
      body: [
        {
          type: "paragraph",
          text: "Akaryakıt istasyonlarında en sessiz suistimallerden biri, bir kart satışının nakit (ya da nakdin kart) olarak kaydedilmesidir. Bu küçük bir veri girişi hatası gibi görünür; oysa banka onaylı toplam ile raporlanan tutar arasındaki bağı koparır ve aradaki farkın cebe atılmasına zemin hazırlar.",
        },
        { type: "heading", text: "Suistimal nasıl işler?" },
        {
          type: "paragraph",
          text: "Kartla ödenen bir satış nakit olarak girildiğinde, sistem o tutarı nakit bekler. Vardiya sonunda nakit eksik çıktığında ise bu eksiklik, gerçek bir açık yerine bir giriş hatası gibi gösterilebilir. Tersi yönde, nakit satış kart gibi girilerek de benzer bir bulanıklık yaratılır.",
        },
        { type: "heading", text: "Neden fark edilmez?" },
        {
          type: "paragraph",
          text: "Satışlar banka kayıtlarıyla karşılaştırılmadığında, otomasyon toplamı kendi içinde tutarlı görünür. Banka onayı devreye girmediği için raporlanan ödeme tipi ile gerçekte bankaya geçen tutar arasındaki uyumsuzluk gizli kalır.",
        },
        { type: "heading", text: "Banka onaylı mutabakat bunu nasıl engeller?" },
        {
          type: "list",
          items: [
            "ERP/otomasyon satışları banka onaylı kayıtlarla eşleştirilir; uyumsuzluk anında görünür.",
            "Ödeme tipi düzeltmesi; orijinal kaydı, düzeltilen tipi, gerekçeyi ve inceleyeni saklar.",
            "Banka onaylı tutarlar, açıklayıcı nakit tutarlardan ayrı izlenir.",
          ],
        },
        { type: "heading", text: "Görünürlük caydırıcıdır" },
        {
          type: "paragraph",
          text: "Her ödeme tipi gerçeğe göre doğrulandığında ve her düzeltme kayıt altına alındığında, bu suistimalin sessizce geçmesi imkânsız hale gelir. Shift Tracker, günlük mutabakatınızı bir suistimal önleme kontrolüne dönüştürür; demo için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Recording a Card Sale as Cash: A Common Station Fraud",
      description:
        "How recording a card sale as cash (or cash as card) becomes fraud, why it goes unnoticed, and how bank-confirmed reconciliation prevents it.",
      body: [
        {
          type: "paragraph",
          text: "One of the quietest frauds at a fuel station is recording a card sale as cash (or cash as card). It looks like a small data-entry slip, but it breaks the link between the bank-confirmed total and the reported figure, creating room to pocket the difference.",
        },
        { type: "heading", text: "How the trick works" },
        {
          type: "paragraph",
          text: "When a card-paid sale is entered as cash, the system expects that amount in cash. If cash comes up short at shift close, the shortfall can be passed off as a data-entry error rather than a real deficit. Entering a cash sale as card creates the same kind of blur in the other direction.",
        },
        { type: "heading", text: "Why it goes unnoticed" },
        {
          type: "paragraph",
          text: "When sales are not compared against bank records, the automation total looks internally consistent. Because bank confirmation never enters the picture, the mismatch between the reported payment type and what actually reached the bank stays hidden.",
        },
        { type: "heading", text: "How bank-confirmed reconciliation prevents it" },
        {
          type: "list",
          items: [
            "ERP/automation sales are matched against bank-confirmed records, so a mismatch shows immediately.",
            "Payment type correction preserves the original entry, the corrected type, the reason and the reviewer.",
            "Bank-confirmed amounts are tracked separately from descriptive cash amounts.",
          ],
        },
        { type: "heading", text: "Visibility is the deterrent" },
        {
          type: "paragraph",
          text: "When every payment type is verified against reality and every correction is on record, this fraud can no longer pass silently. Shift Tracker turns your daily reconciliation into a fraud-prevention control. Contact us for a demo.",
        },
      ],
    },
  },
  {
    slug: "vardiyayi-bir-dakikada-nasil-kapatirsiniz",
    enSlug: "how-to-close-a-shift-in-one-minute",
    date: "2026-07-12",
    tr: {
      title: "Bir Vardiyayı Bir Dakikanın Altında Nasıl Kapatırsınız",
      description:
        "Vardiya kapanışı adım adım: mutabakat sizin için hazırdır, siz yalnızca rakamları gözden geçirip vardiyayı kilitlersiniz. İki saatlik iş bir dakikaya iner.",
      body: [
        {
          type: "paragraph",
          text: "Klasik vardiya kapanışı bir akşamı yiyip bitirir: satışları elle toplamak, kart sliplerini banka tutarlarıyla tek tek karşılaştırmak, nakdin bankaya ulaşıp ulaşmadığını takip etmek. Shift Tracker'da bu iş vardiya kapanmadan çoktan yapılmıştır; size yalnızca sonucu onaylamak kalır. Bu rehber, bir vardiyayı bir dakikanın altında nasıl kapatacağınızı adım adım anlatır.",
        },
        { type: "heading", text: "Siz başlamadan önce sistem ne yaptı?" },
        {
          type: "paragraph",
          text: "Pompa otomasyonunuzdan (TURPAK, Asis) gelen kart satışları, banka onaylı ödemelerle düzenli olarak eşleştirilir. Bankaların gün sonu dosyaları geldikçe kart tarafı arka planda mutabık kılınır; böylece siz vardiyayı gözden geçirmeye oturduğunuzda kart satışları çoktan doğrulanmış olur.",
        },
        { type: "heading", text: "Adım 1: Vardiya incelemesini açın" },
        {
          type: "paragraph",
          text: "Panelde ilgili vardiyayı seçin. Shift Tracker, o vardiyanın ERP toplamlarını, banka onaylı kart tutarlarını ve nakit hareketlerini tek ekranda önünüze getirir. Hiçbir rakamı elle girmezsiniz.",
        },
        { type: "heading", text: "Adım 2: Rakamları gözden geçirin" },
        {
          type: "paragraph",
          text: "Eşleşen satırlar zaten yeşildir; banka ile tutan tutarlar için yapılacak bir şey yoktur. Gözünüz yalnızca dikkat isteyen satırlarda kalır: eksik, fazla veya belirsiz olarak işaretlenmiş kayıtlar.",
        },
        { type: "heading", text: "Adım 3: Nakit yatırmayı doğrulayın" },
        {
          type: "paragraph",
          text: "Nakit yatırma, gerçek banka ekstrenizle karşılaştırılarak doğrulanır. Yatan tutar beklenenle eşleşiyorsa vardiya nakit tarafında da temizdir; eksik veya mükerrer bir yatırma varsa sistem bunu işaretler.",
        },
        { type: "heading", text: "Adım 4: İşaretli farkları çözün" },
        {
          type: "paragraph",
          text: "Çözülmemiş bir fark varsa, açık bir gerekçeyle ilgili vardiyaya ve personele atanır. Kararı siz verirsiniz; sistem her düzeltmeyi, gerekçeyi ve zaman damgasını kayıt altına alır. Böylece açık, kaybolmak yerine denetlenebilir bir kayda dönüşür.",
        },
        { type: "heading", text: "Adım 5: Vardiyayı kilitleyin" },
        {
          type: "paragraph",
          text: "Rakamlar doğrulandığında vardiyayı kilitlersiniz. Kilitli vardiya değiştirilemez ve o anki hesap, kimin neyi onayladığıyla birlikte kalıcı bir kayıt olarak saklanır. Denetime hazırsınız.",
        },
        { type: "heading", text: "Neden iki saat değil, bir dakika?" },
        {
          type: "paragraph",
          text: "Çünkü zor iş, yani eşleştirme ve doğrulama, siz oturmadan önce otomatik yapılmıştır. Size yalnızca kararların verildiği son adım kalır. Manuel karşılaştırmayı ortadan kaldırdığınızda, vardiya kapanışı bir angarya olmaktan çıkıp hızlı bir onaya döner.",
        },
        {
          type: "paragraph",
          text: "Shift Tracker'ı kendi istasyonunuzun rakamlarıyla görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "How to Close a Shift in Under a Minute",
      description:
        "A step-by-step walkthrough of closing a shift: the reconciliation is already done for you, so you just review the figures and lock it. Two hours of work becomes one minute.",
      body: [
        {
          type: "paragraph",
          text: "The classic shift close eats an evening: adding up sales by hand, comparing card slips to bank amounts one by one, chasing whether the cash reached the bank. In Shift Tracker that work is already done before the shift closes, so all that is left for you is to confirm the result. This guide walks through closing a shift in under a minute, step by step.",
        },
        { type: "heading", text: "What the system did before you started" },
        {
          type: "paragraph",
          text: "Card sales from your pump automation (TURPAK, Asis) are matched to bank-confirmed payments on a schedule. As the banks' end-of-day files arrive, the card side is reconciled in the background, so by the time you sit down to review the shift, the card sales are already verified.",
        },
        { type: "heading", text: "Step 1: Open the shift review" },
        {
          type: "paragraph",
          text: "Select the shift in the dashboard. Shift Tracker brings that shift's ERP totals, bank-confirmed card amounts and cash movements onto a single screen. You type no figures by hand.",
        },
        { type: "heading", text: "Step 2: Review the figures" },
        {
          type: "paragraph",
          text: "Matched rows are already green; amounts that agree with the bank need nothing from you. Your attention only lands on the rows that ask for it: records flagged as short, over, or ambiguous.",
        },
        { type: "heading", text: "Step 3: Confirm the cash deposit" },
        {
          type: "paragraph",
          text: "The cash deposit is verified against your real bank statement. If the deposited amount matches what was expected, the shift is clean on the cash side too; if a deposit is short or duplicated, the system flags it.",
        },
        { type: "heading", text: "Step 4: Resolve any flagged differences" },
        {
          type: "paragraph",
          text: "If a difference is unresolved, it is attributed to the relevant shift and employee with a clear reason. You make the call; the system records every correction, its reason and a timestamp. The deficit becomes an auditable record instead of disappearing.",
        },
        { type: "heading", text: "Step 5: Lock the shift" },
        {
          type: "paragraph",
          text: "Once the figures are confirmed, you lock the shift. A locked shift cannot be altered, and its final account is kept as a permanent record of who approved what. You are audit-ready.",
        },
        { type: "heading", text: "Why a minute, not two hours?" },
        {
          type: "paragraph",
          text: "Because the hard part, the matching and verification, is done automatically before you sit down. All that is left for you is the final step where the decisions are made. Once you remove the manual comparison, closing a shift stops being a chore and becomes a quick approval.",
        },
        {
          type: "paragraph",
          text: "Contact us to see Shift Tracker run against your own station's numbers.",
        },
      ],
    },
  },
  {
    slug: "shift-tracker-mutabakati-nasil-calisir",
    enSlug: "how-shift-tracker-reconciliation-works",
    date: "2026-07-11",
    tr: {
      title: "Shift Tracker Mutabakatı Nasıl Çalışır?",
      description:
        "İki bağımsız gerçek (ERP satışları ve banka ödemeleri) nasıl eşleştirilir, sistem neden tam resmi bekler ve her tutar nasıl ispatlanabilir hale gelir.",
      body: [
        {
          type: "paragraph",
          text: "Shift Tracker'ın kalbinde tek bir soru vardır: bu satış gerçekten bankaya geçti mi? Bu soruyu yanıtlamak için sistem iki bağımsız veri kaynağını karşılaştırır. Bu yazı, mutabakatın perde arkasında nasıl çalıştığını anlatır.",
        },
        { type: "heading", text: "İki bağımsız gerçek" },
        {
          type: "paragraph",
          text: "Birincisi, pompa otomasyonundan gelen ERP satışıdır: bu, satış niyetidir. Kartla ödendiği söylenen bir satır, tek başına ödemenin gerçekleştiğinin kanıtı değildir. İkincisi, bankanın POS hareketleridir: bu, gerçek para hareketinin kanıtıdır. Mutabakat, bu ikisini birbirine bağlar.",
        },
        { type: "heading", text: "Otomatik eşleştirme nasıl olur?" },
        {
          type: "paragraph",
          text: "Sistem, kart satışlarını doğru banka hareketiyle eşleştirir: aynı tutar, aynı terminal ve dar bir zaman aralığı. Bir satış birden fazla banka hareketine değil, yalnızca bir hareketle eşleşebilir; böylece aynı ödeme iki kez sayılamaz. Güvenli eşleşmeler onaylanır, belirsiz olanlar incelemeye bırakılır.",
        },
        { type: "heading", text: "Sistem neden tam resmi bekler?" },
        {
          type: "paragraph",
          text: "Bankaların gün sonu dosyaları farklı saatlerde gelir. Sistem, bir günün mutabakatını, o güne ait tüm aktif bankaların dosyaları gelene kadar bekletir. Böylece bir satış, doğru dosyası henüz gelmemişken yanlış bir harekete kilitlenmez. Eksik resimle asla eşleştirme yapılmaz.",
        },
        { type: "heading", text: "Nakit yatırmanın doğrulanması" },
        {
          type: "paragraph",
          text: "Kart tarafı bankayla mutabık kılınırken nakit tarafı da ihmal edilmez. Her nakit yatırma için benzersiz bir referans üretilir; bu referans dekonta yazılır. Sistem, banka ekstresinde bu referansı ve tutarı arar ve yatırmayı otomatik olarak doğrular.",
        },
        { type: "heading", text: "Ekranda ne görürsünüz?" },
        {
          type: "paragraph",
          text: "Her satır üç durumdan birindedir: eşleşti (banka ile tutuyor), inceleme gerekiyor (belirsiz veya tutarsız) veya açık (fark ilgili vardiyaya atandı). Yeşil satırlar için yapılacak bir şey yoktur; dikkatiniz yalnızca gerçekten gerektiren yere gider.",
        },
        { type: "heading", text: "Her tutar ispatlanabilir" },
        {
          type: "paragraph",
          text: "Mutabakat bittiğinde, istasyondaki her tutar gerçek bir banka işlemine dayanır. Her düzeltmenin gerekçesi, sorumlusu ve zamanı kayıtlıdır. Bu, güvene değil kanıta dayalı bir istasyon yönetimidir.",
        },
        {
          type: "paragraph",
          text: "Bu akışın kendi istasyonunuzda nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "How Shift Tracker Reconciliation Works",
      description:
        "How two independent truths (ERP sales and bank payments) get matched, why the system waits for the full picture, and how every amount becomes provable.",
      body: [
        {
          type: "paragraph",
          text: "At the heart of Shift Tracker is a single question: did this sale actually reach the bank? To answer it, the system compares two independent sources of data. This article explains how reconciliation works behind the scenes.",
        },
        { type: "heading", text: "Two independent truths" },
        {
          type: "paragraph",
          text: "The first is the ERP sale from your pump automation: this is the sale intent. A row that says it was paid by card is not, on its own, proof that the payment settled. The second is the bank's POS transactions: this is the evidence of real money movement. Reconciliation links the two.",
        },
        { type: "heading", text: "How automatic matching works" },
        {
          type: "paragraph",
          text: "The system matches a card sale to the right bank transaction: the same amount, the same terminal, and a narrow time window. A sale can be matched to only one bank transaction, never several, so the same payment can never be counted twice. Confident matches are confirmed; ambiguous ones are left for review.",
        },
        { type: "heading", text: "Why the system waits for the full picture" },
        {
          type: "paragraph",
          text: "The banks' end-of-day files arrive at different times. The system holds a day's reconciliation until every active bank's file for that day has landed. That way a sale is never locked onto the wrong transaction while its correct file has not arrived yet. It never matches against an incomplete picture.",
        },
        { type: "heading", text: "Verifying the cash deposit" },
        {
          type: "paragraph",
          text: "While the card side is reconciled with the bank, the cash side is not left out. Each cash deposit gets a unique reference that is written on the deposit slip. The system looks for that reference and amount on your bank statement and verifies the deposit automatically.",
        },
        { type: "heading", text: "What you see on screen" },
        {
          type: "paragraph",
          text: "Every row is in one of three states: matched (it agrees with the bank), needs review (ambiguous or inconsistent), or deficit (the difference was attributed to the relevant shift). Green rows need nothing from you; your attention only goes where it is genuinely needed.",
        },
        { type: "heading", text: "Every amount is provable" },
        {
          type: "paragraph",
          text: "When reconciliation is done, every amount at the station traces back to a real bank transaction. Every correction has a recorded reason, owner and time. This is station management based on proof, not trust.",
        },
        {
          type: "paragraph",
          text: "Contact us to see how this flow works on your own station.",
        },
      ],
    },
  },
  {
    slug: "kredi-karti-gun-sonu-raporu-banka-mutabakati",
    enSlug: "pos-end-of-day-report-vs-bank-settlement",
    date: "2026-07-14",
    tr: {
      title: "Kredi Kartı Gün Sonu Raporu Banka ile Eşleşiyor mu? Mutabakat Yapıldı mı?",
      description:
        "POS cihazından çıkan gün sonu (Z) raporundaki kart tutarı, bankaya geçen tutarla birebir tutmalı. Ama bu kağıt rapor mükerrer, başka güne ait, başka bir firmadan ya da nakit açığını gizlemek için düzeltilmiş olabilir. Tek güvenilir kanıt bankanın kendi kaydıdır.",
      body: [
        {
          type: "paragraph",
          text: "Çoğu istasyonda gün sonu kontrolü tek bir kağıda dayanır: POS cihazından alınan gün sonu (Z) raporu. Mantık basit görünür; cihazın yazdığı kart toplamı, bankaya geçen tutarla birebir tutmalıdır. Peki o kağıdın kendisi ne kadar güvenilir? Bu yazıda, gün sonu raporuna güvenmenin neden yeterli olmadığını ve gerçek mutabakatın nasıl yapıldığını anlatıyoruz.",
        },
        { type: "heading", text: "Gün sonu (Z) raporu neyi kanıtlar, neyi kanıtlamaz?" },
        {
          type: "paragraph",
          text: "Gün sonu raporu, POS cihazının o gün çektiği kart işlemlerinin cihaz üzerindeki toplamıdır. Bu, cihazın kendi beyanıdır; paranın gerçekten hesabınıza geçtiğinin kanıtı değildir. Kanıt yalnızca bankanın kendi kaydında, yani hesabınıza düşen valörlü tutarda vardır.",
        },
        { type: "heading", text: "Kağıt gün sonu raporu neden yanıltıcı olabilir?" },
        {
          type: "list",
          items: [
            "Mükerrer: Aynı gün sonu raporu birden fazla kez kullanılıp farklı açıkların üstünü kapatabilir.",
            "Başka güne ait: Bir günün raporu, başka bir günün kasasını denk göstermek için kullanılabilir.",
            "Başka bir firmadan: Benzer bir işletmenin ya da başka bir cihazın raporu, sizinki gibi sunulabilir.",
            "Düzeltilmiş kopya: Nakit açığını gizlemek için tutarı düşürülmüş, elden geçirilmiş bir kopya verilebilir.",
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Ortak nokta şudur: kağıt bir rapor, onu üreten kişinin beyanıdır ve elle değiştirilebilir. Bu tür " },
            { text: "dolandırıcılık açıkları", to: "/fraud-prevention/" },
            { text: ", sıkı bir denetim ve banka doğrulaması olmadan görünmez kalır." },
          ],
        },
        { type: "heading", text: "Gerçek mutabakat: kart satışını bankanın kaydıyla eşleştirmek" },
        {
          type: "paragraph",
          segments: [
            { text: "Doğru soru 'gün sonu raporu kasayla tutuyor mu' değil, 'her kart satışı bankanın kaydıyla eşleşiyor mu' olmalı. Shift Tracker, otomasyondan gelen her kart satışını doğrudan bankanın gün sonu mutabakat dosyasıyla eşleştirir; " },
            { text: "mutabakatın nasıl çalıştığını buradan görebilirsiniz", to: "/blog/shift-tracker-mutabakati-nasil-calisir/" },
            { text: ". Kanıt cihazın kağıdı değil, bankanın kendi kaydıdır." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Bu, aynı zamanda bir " },
            { text: "kredi kartı ödeme doğrulama", to: "/kredi-karti-odeme-dogrulama/" },
            { text: " katmanıdır: kartla ödendiği söylenen her tutar, gerçekten bankaya geçip geçmediğine göre teyit edilir." },
          ],
        },
        { type: "heading", text: "Artık kağıt gün sonu raporuyla uğraşmanıza gerek yok" },
        {
          type: "paragraph",
          segments: [
            { text: "Kart tarafı banka kaydıyla otomatik doğrulandığında, gün sonu raporunu elle toplama, karşılaştırma ve saklama zahmeti ortadan kalkar. Vardiya siz açtığınızda çoktan mutabık kılınmıştır; size yalnızca gözden geçirip kilitlemek kalır. Nasıl olduğunu " },
            { text: "bir vardiyayı bir dakikanın altında kapatma", to: "/blog/vardiyayi-bir-dakikada-nasil-kapatirsiniz/" },
            { text: " yazısında adım adım anlattık." },
          ],
        },
        {
          type: "paragraph",
          text: "Özetle: kağıt gün sonu raporu bir başlangıç noktasıdır, kanıt değildir. Kanıt, her kuruşun bankanın kendi kaydına dayanmasıdır. İstasyonunuzda bunun nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Does Your POS End-of-Day Report Match the Bank? Has It Been Reconciled?",
      description:
        "The card total on the POS end-of-day (Z) slip should match the amount that reached the bank to the last kurus. But that paper slip can be a duplicate, from another day, from another company, or a doctored copy that hides skimmed cash. The only reliable proof is the bank's own record.",
      body: [
        {
          type: "paragraph",
          text: "At most stations the end-of-day check rests on a single piece of paper: the end-of-day (Z) report printed by the POS device. The logic looks simple; the card total the device prints should match the amount that reached the bank exactly. But how trustworthy is that piece of paper itself? This article explains why trusting the end-of-day report is not enough, and how real reconciliation is done.",
        },
        { type: "heading", text: "What the end-of-day (Z) report does and does not prove" },
        {
          type: "paragraph",
          text: "The end-of-day report is the device's own total of the card transactions it ran that day. It is the device's own statement; it is not proof that the money actually reached your account. That proof exists only in the bank's own record, in the settled amount that lands in your account.",
        },
        { type: "heading", text: "Why a paper end-of-day report can mislead" },
        {
          type: "list",
          items: [
            "Duplicate: the same end-of-day report is used more than once to cover different shortfalls.",
            "From another day: one day's report is used to make another day's register look balanced.",
            "From another company: a similar business's or another device's report is presented as yours.",
            "Doctored copy: a reworked copy with a reduced total is handed over to hide skimmed cash.",
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "The common thread: a paper report is the statement of whoever produced it, and it can be altered by hand. These kinds of " },
            { text: "fraud gaps", to: "/en/fraud-prevention/" },
            { text: " stay invisible without a tight audit and bank verification." },
          ],
        },
        { type: "heading", text: "Real reconciliation: matching the card sale to the bank's record" },
        {
          type: "paragraph",
          segments: [
            { text: "The right question is not 'does the end-of-day report match the register' but 'does every card sale match the bank's record'. Shift Tracker matches every card sale from your automation directly to the bank's end-of-day settlement file; you can " },
            { text: "see how reconciliation works here", to: "/en/blog/how-shift-tracker-reconciliation-works/" },
            { text: ". The proof is the bank's own record, not the device's paper." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "This is also a layer of " },
            { text: "credit card payment verification", to: "/en/credit-card-payment-verification/" },
            { text: ": every amount said to be paid by card is confirmed by whether it actually reached the bank." },
          ],
        },
        { type: "heading", text: "You no longer have to deal with the paper end-of-day report" },
        {
          type: "paragraph",
          segments: [
            { text: "Once the card side is verified against the bank automatically, the chore of adding up, comparing and filing the end-of-day report disappears. The shift is already reconciled by the time you open it; all that is left is to review and lock it. We walk through exactly how in " },
            { text: "how to close a shift in under a minute", to: "/en/blog/how-to-close-a-shift-in-one-minute/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          text: "In short: the paper end-of-day report is a starting point, not proof. Proof is every amount tracing back to the bank's own record. Contact us to see how this works on your own station.",
        },
      ],
    },
  },
  {
    slug: "kredi-karti-satisi-bankaya-ne-zaman-gecer",
    enSlug: "when-do-card-sales-reach-the-bank",
    date: "2026-07-13",
    tr: {
      title: "Kredi Kartı Satışı Bankaya Ne Zaman Geçer? Valör ve Blokaj Rehberi",
      description:
        "Kart satışını yaptınız ama para neden aynı gün hesapta görünmüyor? Valör, blokaj, komisyon ve taksitin POS toplamını banka tutarından neden ayırdığını ve doğru mutabakatın ne zaman yapılabileceğini anlatıyoruz.",
      body: [
        {
          type: "paragraph",
          text: "Kart satışını yaptınız ama para neden aynı gün hesapta görünmüyor? Bu bir hata değil; kredi kartı ödemelerinin bankaya geçiş biçimidir. Bu yazıda valör, blokaj, komisyon ve taksitin POS toplamını neden banka tutarından ayırdığını anlatıyoruz.",
        },
        { type: "heading", text: "Kısa cevap: kart parası genelde ertesi gün (valör) hesaba geçer" },
        {
          type: "paragraph",
          text: "Bir kart satışı anında gerçekleşse de, bankaya geçişi anlık değildir. Banka, tutarı belirli bir valör (değer) gününde hesabınıza aktarır. Bu yüzden bir vardiyanın kart satışları, çoğu zaman ancak ertesi gün banka tarafında görünür.",
        },
        { type: "heading", text: "Valör ve blokaj nedir?" },
        {
          type: "paragraph",
          text: "Valör, bir işlemin gerçekten hesaba geçtiği değer günüdür. Blokaj ise, tutarın hesaba aktarılmadan önce bekletildiği süredir. İkisi birlikte, POS'ta gördüğünüz satış anı ile bankada gördüğünüz para hareketi arasındaki farkı yaratır.",
        },
        { type: "heading", text: "POS toplamı neden banka tutarına birebir eşit değildir?" },
        {
          type: "list",
          items: [
            "Komisyon: Banka, tutardan işlem komisyonunu keser; hesaba geçen net tutardır.",
            "Taksitli satış: Taksitli işlemler hesaba parça parça, farklı günlerde yansıyabilir.",
            "Valör farkı: Bir vardiyanın satışı, banka tarafında ertesi güne kayar.",
            "İade ve iptal: Sonradan yapılan iade veya iptaller banka tutarını değiştirir.",
          ],
        },
        { type: "heading", text: "Peki mutabakat ne zaman yapılabilir?" },
        {
          type: "paragraph",
          segments: [
            { text: "Doğru eşleştirme için bankanın gün sonu mutabakat dosyasının gelmesi gerekir; bu dosyalar çoğunlukla ertesi sabah düşer. Bu yüzden kart tarafı mutabakatı anlık değil, planlı çalışır ve bir sonraki vardiyayı açtığınızda hazırdır. " },
            { text: "Mutabakatın nasıl çalıştığını", to: "/blog/shift-tracker-mutabakati-nasil-calisir/" },
            { text: " ayrı bir yazıda ayrıntılı anlattık." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Bu bekleme aslında bir güvencedir: eksik resimle eşleştirme yapılmaz, böylece bir satış yanlış bir harekete kilitlenmez. Sonuç, güvenilir bir " },
            { text: "kredi kartı ödeme doğrulaması", to: "/kredi-karti-odeme-dogrulama/" },
            { text: ": her kart satışı, gerçekten bankaya geçtiği tutarla teyit edilir." },
          ],
        },
        {
          type: "paragraph",
          text: "Özetle, POS toplamının aynı gün banka tutarına eşit olmaması normaldir. Önemli olan, her satışın doğru gün ve doğru tutarla bankaya eşleştiğinin kanıtlanmasıdır. İstasyonunuzda bunun nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "When Do Card Sales Reach the Bank? A Guide to Settlement Timing",
      description:
        "You made the card sale, so why is the money not in the account the same day? We explain how value date, holds, commission and installments separate the POS total from the bank amount, and when real reconciliation can be done.",
      body: [
        {
          type: "paragraph",
          text: "You made the card sale, so why is the money not in the account the same day? This is not an error; it is how card payments reach the bank. This article explains how the value date, holds, commission and installments separate the POS total from the bank amount.",
        },
        { type: "heading", text: "Short answer: card money usually lands the next day (value date)" },
        {
          type: "paragraph",
          text: "Even though a card sale happens instantly, it does not reach the bank instantly. The bank transfers the amount to your account on a specific value date. That is why a shift's card sales usually only appear on the bank side the following day.",
        },
        { type: "heading", text: "What are value date and holds?" },
        {
          type: "paragraph",
          text: "The value date is the day a transaction actually credits the account. A hold is the period the amount is kept before it is transferred. Together they create the gap between the moment of sale you see on the POS and the money movement you see at the bank.",
        },
        { type: "heading", text: "Why the POS total does not exactly equal the bank amount" },
        {
          type: "list",
          items: [
            "Commission: the bank deducts a transaction fee; what lands is the net amount.",
            "Installments: installment transactions can credit the account in parts, on different days.",
            "Value date: a shift's sales shift to the next day on the bank side.",
            "Refunds and cancellations: later refunds or cancellations change the bank amount.",
          ],
        },
        { type: "heading", text: "So when can reconciliation actually be done?" },
        {
          type: "paragraph",
          segments: [
            { text: "Correct matching needs the bank's end-of-day settlement file, which usually lands the next morning. That is why card-side reconciliation runs on a schedule, not instantly, and is ready by the time you open the next shift. We explain " },
            { text: "how reconciliation works", to: "/en/blog/how-shift-tracker-reconciliation-works/" },
            { text: " in detail in a separate article." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "That wait is actually a safeguard: no matching happens against an incomplete picture, so a sale is never locked onto the wrong transaction. The result is reliable " },
            { text: "credit card payment verification", to: "/en/credit-card-payment-verification/" },
            { text: ": every card sale is confirmed by the amount that actually reached the bank." },
          ],
        },
        {
          type: "paragraph",
          text: "In short, it is normal for the POS total not to equal the bank amount on the same day. What matters is proving that every sale matched the bank on the right day and for the right amount. Contact us to see how this works on your own station.",
        },
      ],
    },
  },
  {
    slug: "z-raporu-vardiya-raporu-neden-tutmaz",
    enSlug: "why-z-report-doesnt-match-shift-report",
    date: "2026-07-13",
    tr: {
      title: "Z Raporu ile Vardiya Raporu Neden Tutmaz? (ve Nasıl Çözülür)",
      description:
        "Z raporu gün sonunda kesilir ve vardiya sınırlarıyla çakışmaz; ödeme tipleri de karışır. Bu yüzden kasa bir türlü denk gelmez. Farkın nedenini ve vardiya bazlı mutabakatın bunu nasıl çözdüğünü anlatıyoruz.",
      body: [
        {
          type: "paragraph",
          text: "Z raporunuz kasayla ya da vardiya raporuyla bir türlü tutmuyor mu? Yalnız değilsiniz. Bunun nedeni çoğu zaman bir hata değil, raporların hangi zaman aralığını kapsadığıdır. Bu yazıda farkın nereden geldiğini ve nasıl çözüldüğünü anlatıyoruz.",
        },
        { type: "heading", text: "Kısa cevap: Z raporu gün sonunda kesilir, vardiya sınırlarıyla çakışmaz" },
        {
          type: "paragraph",
          text: "Z raporu genellikle gün sonunda (ör. 23:59) alınır ve o güne ait tüm işlemleri tek bir paket olarak toplar. Vardiyalar ise gün içinde değişir. Bu nedenle bir vardiyanın satışı ile bir günün Z raporu aynı zaman aralığını kapsamaz ve tutarlar doğal olarak kaymış görünür.",
        },
        { type: "heading", text: "Z raporu, X raporu ve vardiya raporu arasındaki fark" },
        {
          type: "paragraph",
          text: "X raporu, gün içinde alınan ve kasayı sıfırlamayan bir ara özettir. Z raporu, günü kapatan ve toplamı sıfırlayan gün sonu raporudur. Vardiya raporu ise tek bir personelin çalıştığı süreyi kapsar. Üçü farklı zaman aralıklarına baktığı için birebir eşleşmeleri beklenmez.",
        },
        { type: "heading", text: "Z raporu neden kasayla ve vardiyayla tutmaz?" },
        {
          type: "list",
          items: [
            "Gün kayması: Z raporu gün sonunda kesilir, vardiya ise gün ortasında biter.",
            "Ödeme tipi karışıklığı: Kartla ödenen bir satış nakit girilmiş olabilir; kasa tutmaz.",
            "Vardiya ortası nakit: Devir sırasında alınan nakit hangi vardiyaya yazıldığına göre değişir.",
            "İade zamanlaması: Sonradan yapılan iadeler, ait olmadıkları vardiyada görünebilir.",
          ],
        },
        { type: "heading", text: "Farkı her seferinde elle çözmek yerine" },
        {
          type: "paragraph",
          segments: [
            { text: "Çözüm, tek bir kağıda güvenmek değil, iki bağımsız kaynağı, yani otomasyon satışlarını ve banka kayıtlarını her vardiya bazında eşleştirmektir. " },
            { text: "Mutabakatın nasıl çalıştığını", to: "/blog/shift-tracker-mutabakati-nasil-calisir/" },
            { text: " anlatan yazımızda bu iki-kaynak yönteminin neden doğru sonucu verdiğini görebilirsiniz." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "Bu yaklaşımla vardiya, siz açtığınızda çoktan mutabık kılınmış olur; kasanın neden tutmadığını saatlerce aramak yerine " },
            { text: "vardiyayı bir dakikanın altında kapatırsınız", to: "/blog/vardiyayi-bir-dakikada-nasil-kapatirsiniz/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          text: "Özetle, Z raporunun vardiyayla tutmaması bir arıza değil, farklı zaman aralıklarının doğal sonucudur. Doğru çözüm, her vardiyayı banka onaylı kayıtlarla mutabık kılmaktır. İstasyonunuzda bunun nasıl çalıştığını görmek için bizimle iletişime geçin.",
        },
      ],
    },
    en: {
      title: "Why Your Z-Report Doesn't Match the Shift Report (and How to Fix It)",
      description:
        "The Z-report is cut at the end of the day and does not line up with shift boundaries, and payment types get mixed. That is why the register never balances cleanly. We explain the cause and how per-shift reconciliation fixes it.",
      body: [
        {
          type: "paragraph",
          text: "Does your Z-report never quite match the register or the shift report? You are not alone. The cause is usually not an error but which time window each report covers. This article explains where the difference comes from and how it is resolved.",
        },
        { type: "heading", text: "Short answer: the Z-report is cut at day end, not at shift boundaries" },
        {
          type: "paragraph",
          text: "The Z-report is usually taken at the end of the day (for example 23:59) and totals all of that day's transactions in a single batch. Shifts, however, change during the day. So a shift's sales and a day's Z-report do not cover the same time window, and the totals naturally look shifted.",
        },
        { type: "heading", text: "The difference between the Z-report, X-report and shift report" },
        {
          type: "paragraph",
          text: "The X-report is an interim summary taken during the day that does not reset the register. The Z-report is the end-of-day report that closes the day and resets the total. The shift report covers a single employee's time on duty. Because all three look at different time windows, they are not expected to match exactly.",
        },
        { type: "heading", text: "Why the Z-report does not match the register and the shift" },
        {
          type: "list",
          items: [
            "Day shift: the Z-report is cut at day end, while a shift ends in the middle of the day.",
            "Payment-type mix-up: a card sale may have been entered as cash; the register will not balance.",
            "Mid-shift cash: cash taken during handover depends on which shift it was recorded against.",
            "Refund timing: later refunds can appear in a shift they do not belong to.",
          ],
        },
        { type: "heading", text: "Instead of resolving the gap by hand every time" },
        {
          type: "paragraph",
          segments: [
            { text: "The fix is not to trust a single piece of paper, but to match two independent sources, the automation sales and the bank records, on a per-shift basis. Our article on " },
            { text: "how reconciliation works", to: "/en/blog/how-shift-tracker-reconciliation-works/" },
            { text: " shows why this two-source method produces the right result." },
          ],
        },
        {
          type: "paragraph",
          segments: [
            { text: "With this approach the shift is already reconciled by the time you open it; instead of hunting for hours why the register does not balance, you " },
            { text: "close a shift in under a minute", to: "/en/blog/how-to-close-a-shift-in-one-minute/" },
            { text: "." },
          ],
        },
        {
          type: "paragraph",
          text: "In short, a Z-report not matching the shift is not a fault but the natural result of different time windows. The right fix is to reconcile every shift against bank-confirmed records. Contact us to see how this works on your own station.",
        },
      ],
    },
  },
];

export function getPostBySlug(slug?: string): BlogPost | undefined {
  return blogPosts.find((post) => post.slug === slug);
}

/** Resolve a blog post by the URL slug for the active language. */
export function getPostForLang(
  slug: string | undefined,
  isEnglish: boolean
): BlogPost | undefined {
  return blogPosts.find((p) => (isEnglish ? p.enSlug : p.slug) === slug);
}

export function getSortedPosts(): BlogPost[] {
  return [...blogPosts].sort((a, b) => b.date.localeCompare(a.date));
}

/**
 * Canonical URL for a post in the given language (trailing slash, /en prefix
 * and enSlug for English). Single home for the slug-vs-enSlug URL contract:
 * use this instead of hand-building post hrefs.
 */
export function getPostHref(post: BlogPost, lang: BlogLang): string {
  return lang === "en" ? `/en/blog/${post.enSlug}/` : `/blog/${post.slug}/`;
}

/** Shared "read more" link label, used by the blog index and related posts. */
export const readMoreLabel: Record<BlogLang, string> = {
  tr: "Devamını oku",
  en: "Read more",
};

export function formatBlogDate(date: string, lang: BlogLang): string {
  try {
    // Post dates are date-only ISO strings, parsed as UTC midnight. Format in
    // UTC too, or visitors west of UTC see the previous day - and the client
    // render then disagrees with the UTC build-time prerender (hydration
    // mismatch).
    return new Intl.DateTimeFormat(lang === "en" ? "en-US" : "tr-TR", {
      year: "numeric",
      month: "long",
      day: "numeric",
      timeZone: "UTC",
    }).format(new Date(date));
  } catch {
    return date;
  }
}
